import React, {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useApp} from '../context/AppContext';

const {FiPlus, FiTrash2, FiSave, FiBook, FiCalendar, FiClock, FiInfo, FiX, FiDownload, FiUpload} = FiIcons;

const SetPLO = () => {
  const {state, dispatch, assessmentService} = useApp();
  const [ploStatements, setPloStatements] = useState({});
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    if (state.activeCourse) {
      loadPLOData();
    } else {
      // Initialize with empty PLO1
      setPloStatements({PLO1: ''});
    }
    loadTemplates(); // Load templates on component mount
  }, [state.activeCourse]);

  const loadPLOData = async () => {
    if (!state.activeCourse) return;
    setLoading(true);
    try {
      const {data, error} = await assessmentService.getPLOStatements(state.activeCourse.id);
      if (error) throw new Error(error);
      if (data && data.length > 0) {
        const ploObj = data.reduce((acc, plo) => {
          acc[plo.plo_code] = plo.statement;
          return acc;
        }, {});
        setPloStatements(ploObj);
      } else {
        // Initialize with empty PLO1 if no data exists
        setPloStatements({PLO1: ''});
      }
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message});
    } finally {
      setLoading(false);
    }
  };

  const updatePLOStatement = (plo, statement) => {
    setPloStatements({...ploStatements, [plo]: statement});
  };

  const addPLO = () => {
    const existingPLOs = Object.keys(ploStatements);
    const existingNumbers = existingPLOs.map(plo => parseInt(plo.replace('PLO', '')));
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    
    // Check if we can add more PLOs (limit to 11)
    if (maxNumber >= 11) {
      alert('Maximum 11 PLOs allowed');
      return;
    }
    
    const newPLONumber = maxNumber + 1;
    const newPLO = `PLO${newPLONumber}`;
    setPloStatements({...ploStatements, [newPLO]: ''});
  };

  const removePLO = (ploToRemove) => {
    // Don't allow removing if it's the only PLO
    if (Object.keys(ploStatements).length === 1) {
      alert('At least one PLO must remain');
      return;
    }
    
    const {[ploToRemove]: removed, ...remaining} = ploStatements;
    setPloStatements(remaining);
  };

  const saveData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    // Check if all PLOs have content
    const emptyPLOs = Object.entries(ploStatements).filter(([_, statement]) => statement.trim() === '');
    if (emptyPLOs.length > 0) {
      alert('Please fill in all PLO statements before saving');
      return;
    }

    setLoading(true);
    try {
      const {error} = await assessmentService.savePLOStatements(
        state.activeCourse.id,
        ploStatements
      );
      if (error) throw new Error(error);

      // Update local state
      dispatch({type: 'SET_PLO_STATEMENTS', payload: ploStatements});
      alert('PLO statements saved successfully!');
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message});
      alert('Error saving PLO statements: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Template functions (fixes issue #5)
  const loadTemplates = async () => {
    try {
      const {data, error} = await assessmentService.getPLOTemplates();
      if (error) throw new Error(error);
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading PLO templates:', error);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    // Check if all PLOs have content
    const emptyPLOs = Object.entries(ploStatements).filter(([_, statement]) => statement.trim() === '');
    if (emptyPLOs.length > 0) {
      alert('Please fill in all PLO statements before saving as template');
      return;
    }

    setTemplateLoading(true);
    try {
      const {error} = await assessmentService.savePLOTemplate(templateName, ploStatements);
      if (error) throw new Error(error);
      
      alert('PLO template saved successfully!');
      setShowTemplateModal(false);
      setTemplateName('');
      loadTemplates(); // Refresh templates
    } catch (error) {
      alert('Error saving template: ' + error.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  const loadFromTemplate = async () => {
    if (!selectedTemplate) {
      alert('Please select a template');
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (template) {
      setPloStatements(template.plo_statements);
      setSelectedTemplate('');
      alert('Template loaded successfully!');
    }
  };

  // Sort PLOs by number for display
  const sortedPLOs = Object.entries(ploStatements).sort((a, b) => {
    const numA = parseInt(a[0].replace('PLO', ''));
    const numB = parseInt(b[0].replace('PLO', ''));
    return numA - numB;
  });

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to set up Programme Learning Outcomes (PLOs).</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="space-y-6"
    >
      {/* Course Info Header - Enhanced */}
      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
        <h2 className="text-xl font-bold text-green-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="font-medium text-green-700">Faculty:</span>
            <p className="text-green-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-green-700">Programme:</span>
            <p className="text-green-600">{state.activeCourse.programme}</p>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-green-700" />
            <div>
              <span className="font-medium text-green-700">Semester:</span>
              <p className="text-green-600">{state.activeCourse.semester}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-green-700" />
            <div>
              <span className="font-medium text-green-700">Session:</span>
              <p className="text-green-600">{state.activeCourse.session}</p>
            </div>
          </div>
          <div>
            <span className="font-medium text-green-700">Credit Hours:</span>
            <p className="text-green-600">{state.activeCourse.credit_hours || state.activeCourse.creditHours}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Table 0: Programme Learning Outcomes (PLO)</h2>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              {Object.keys(ploStatements).length}/11 PLOs
            </div>
            <button
              onClick={() => setShowGuide(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>PLO Importance Guide</span>
            </button>
          </div>
        </div>

        {/* PLO Statements Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left w-24">PLO</th>
                <th className="border border-gray-300 px-4 py-2 text-left">PLO Statement/Description</th>
                <th className="border border-gray-300 px-4 py-2 text-left w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPLOs.map(([plo, statement]) => (
                <tr key={plo}>
                  <td className="border border-gray-300 px-4 py-2 font-medium bg-gray-50">
                    {plo}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <textarea
                      value={statement}
                      onChange={(e) => updatePLOStatement(plo, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      placeholder="Enter PLO statement/description"
                      rows="3"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <button
                      onClick={() => removePLO(plo)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove PLO"
                      disabled={Object.keys(ploStatements).length === 1}
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <button
              onClick={addPLO}
              disabled={Object.keys(ploStatements).length >= 11}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Add PLO</span>
            </button>
            
            {/* Template Load Dropdown */}
            <div className="flex items-center space-x-2">
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.template_name}
                  </option>
                ))}
              </select>
              <button
                onClick={loadFromTemplate}
                disabled={!selectedTemplate}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Load</span>
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>Save as Template</span>
            </button>
            <button
              onClick={saveData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save PLO Statements'}</span>
            </button>
          </div>
        </div>

        {/* Information Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• Define Programme Learning Outcomes (PLOs) for your programme</li>
            <li>• You can add up to 11 PLOs for your programme</li>
            <li>• Each PLO should clearly describe what students will achieve</li>
            <li>• These PLOs will be used in the CLO-PLO mapping later</li>
            <li>• All PLO statements must be filled before saving</li>
          </ul>
        </div>

        {/* Next Steps */}
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-semibold text-yellow-900 mb-2">Next Steps:</h4>
          <ol className="text-yellow-800 space-y-1 list-decimal list-inside text-sm">
            <li>Complete all PLO statements above</li>
            <li>Save the PLO statements</li>
            <li>Move to Assessment Plan to define Course Learning Outcomes (CLOs)</li>
            <li>Use CLO-PLO Mapping to link your CLOs with these PLOs</li>
          </ol>
        </div>
      </div>

      {/* Template Save Modal */}
      <AnimatePresence>
        {showTemplateModal && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowTemplateModal(false)}
          >
            <motion.div
              initial={{scale: 0.95, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.95, opacity: 0}}
              className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Save PLO Template</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowTemplateModal(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTemplate}
                    disabled={templateLoading || !templateName.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {templateLoading ? 'Saving...' : 'Save Template'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PLO Importance Guide Modal */}
      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowGuide(false)}
          >
            <motion.div
              initial={{scale: 0.95, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.95, opacity: 0}}
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Understanding PLO Importance & Mapping</h2>
                <button
                  onClick={() => setShowGuide(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* PLO Definition */}
                <section className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-900 mb-3">What are Programme Learning Outcomes (PLOs)?</h3>
                  <p className="text-purple-800 text-sm leading-relaxed">
                    PLOs are statements that describe the specific knowledge, skills, attitudes, and abilities that graduates should demonstrate upon graduation. They define what students are expected to achieve after completing all courses in their programme and indicate the programme's disciplinary identity.
                  </p>
                  <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                    <p className="text-purple-900 text-sm font-medium">
                      <strong>Example PLO Format:</strong> "At the end of the Bachelor of Engineering, students will be able to..."
                    </p>
                  </div>
                </section>

                {/* PLO Formulation Guidelines */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">PLO Formulation Guidelines</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">SMART Principles</h4>
                      <ul className="text-blue-800 text-sm space-y-1">
                        <li><strong>Specific:</strong> Clear and precise statements</li>
                        <li><strong>Measurable:</strong> Can be assessed and evaluated</li>
                        <li><strong>Achievable:</strong> Realistic for students to attain</li>
                        <li><strong>Relevant:</strong> Aligned with programme goals</li>
                        <li><strong>Time-bound:</strong> Achievable within programme duration</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2">Writing Best Practices</h4>
                      <ul className="text-green-800 text-sm space-y-1">
                        <li>• Use action verbs to specify desired performance</li>
                        <li>• Keep statements brief and focused</li>
                        <li>• Focus on one outcome per statement</li>
                        <li>• Align with MQF qualification descriptors</li>
                        <li>• Consider stakeholder needs and expectations</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* PLO Measurement Framework */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">PLO Measurement & Assessment Framework</h3>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Direct Measurement Methods</h4>
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <ul className="text-yellow-800 text-sm space-y-2">
                        <li><strong>Student Performance Data:</strong> Mid-term and final examinations, assignment scores, project evaluations, final marks</li>
                        <li><strong>Portfolio Assessment:</strong> Collection of student work demonstrating PLO achievement over time</li>
                        <li><strong>Capstone Projects:</strong> Comprehensive projects that integrate multiple PLOs</li>
                        <li><strong>Standardized Tests:</strong> External assessments aligned with programme outcomes</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Indirect Measurement Methods</h4>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <ul className="text-orange-800 text-sm space-y-2">
                        <li><strong>Graduate Surveys:</strong> Self-assessment by graduates on their preparation and competencies</li>
                        <li><strong>Employer Feedback:</strong> Evaluations from employers on graduate performance</li>
                        <li><strong>Alumni Tracking:</strong> Career progression and professional development status</li>
                        <li><strong>Focus Group Discussions:</strong> Qualitative feedback from graduates and employers</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* PLO-CLO Mapping Importance */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Why PLO-CLO Mapping Matters</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-2">Constructive Alignment</h4>
                      <p className="text-indigo-800 text-sm">
                        PLO-CLO mapping ensures that course-level learning outcomes (CLOs) directly contribute to programme-level outcomes, creating a coherent curriculum structure.
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <h4 className="font-semibold text-red-900 mb-2">Quality Assurance</h4>
                      <p className="text-red-800 text-sm">
                        Explicit mapping allows for systematic monitoring and evaluation of programme effectiveness through measurable course-level data.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Curriculum Development Process</h4>
                    <div className="text-gray-800 text-sm space-y-2">
                      <p><strong>1. Educational Goals → PEOs → PLOs:</strong> Link institutional goals to programme outcomes</p>
                      <p><strong>2. PLOs → CLOs:</strong> Map programme outcomes to course-level outcomes</p>
                      <p><strong>3. CLOs → Assessment:</strong> Align assessments with learning outcomes</p>
                      <p><strong>4. Assessment → Evidence:</strong> Use student performance as evidence of PLO attainment</p>
                    </div>
                  </div>
                </section>

                {/* Measurement Timeline */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">PLO Assessment Timeline</h3>
                  <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="font-semibold text-teal-900 mb-2">During Programme</h4>
                        <ul className="text-teal-800 space-y-1">
                          <li>• Continuous assessment through CLO measurements</li>
                          <li>• Portfolio development and evaluation</li>
                          <li>• Milestone assessments at key programme points</li>
                          <li>• Student self-assessment and reflection</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-teal-900 mb-2">Post-Graduation</h4>
                        <ul className="text-teal-800 space-y-1">
                          <li>• Graduate surveys (within 2-5 years)</li>
                          <li>• Employer feedback collection</li>
                          <li>• Career progression tracking</li>
                          <li>• Professional development assessment</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Stakeholder Engagement */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Stakeholder Engagement in PLO Development</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-2">Industry Partners</h4>
                      <p className="text-purple-800 text-sm">
                        Employers provide input on required competencies and skills needed in the workforce.
                      </p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Alumni</h4>
                      <p className="text-blue-800 text-sm">
                        Graduates share experiences about programme preparation for professional life.
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2">External Experts</h4>
                      <p className="text-green-800 text-sm">
                        Subject matter experts ensure PLOs meet disciplinary standards and expectations.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Continuous Quality Improvement */}
                <section className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Continuous Quality Improvement (CQI)</h3>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-amber-800 text-sm mb-3">
                      <strong>Regular Review Process:</strong> PLOs should be monitored and reviewed regularly to ensure currency and relevance through:
                    </p>
                    <ul className="text-amber-800 text-sm space-y-1">
                      <li>• Analysis of PLO attainment data from course assessments</li>
                      <li>• Stakeholder feedback and engagement</li>
                      <li>• Curriculum evaluation and improvement initiatives</li>
                      <li>• Alignment with institutional strategic goals</li>
                      <li>• Compliance with regulatory and accreditation requirements</li>
                    </ul>
                  </div>
                </section>

                {/* Call to Action */}
                <section className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Define Your PLOs?</h3>
                  <p className="text-gray-700 text-sm mb-3">
                    Use the guidelines above to create meaningful PLO statements that will guide your programme's curriculum design and assessment strategy.
                  </p>
                  <button
                    onClick={() => setShowGuide(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Start Defining PLOs
                  </button>
                </section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SetPLO;